import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, ArrowLeft, ArrowRight, Share2, Bookmark } from 'lucide-react';
import { motion } from 'framer-motion';
export function BlogPost() {
  // This is an example blog post - in a real app, you'd fetch this data based on the slug
  const post = {
    title: 'The Architecture of Exclusion: How Public Spaces Are Designed to Police Fat Bodies',
    category: 'essay',
    author: {
      name: 'Jordan Miller',
      bio: 'Jordan Miller is a fat writer and urban planner based in Chicago. Their work examines how the built environment shapes who gets to move through public space with dignity and ease. They hold an MFA in Creative Nonfiction from Columbia University.',
      image: 'bg-brand-magenta'
    },
    date: '2024-01-15',
    readTime: '12 min read',
    content: `
      <p>I'm standing in front of a subway turnstile in Manhattan, and I know—before I even try—that I won't fit through. The metal bars are spaced exactly 18 inches apart. My hips measure 52 inches. The math is simple, but the message is complex: <em>You don't belong here.</em></p>

      <p>This isn't an accident. It's architecture.</p>

      <h2>The Hostile Design of Everyday Spaces</h2>

      <p>We talk about accessibility in terms of ramps and elevators, braille and closed captions. But what about the ways public space is designed to exclude fat bodies? What about the restaurant booth with a fixed table that doesn't accommodate bellies? The airplane seat that assumes everyone is under 200 pounds? The park bench with armrests strategically placed to prevent anyone from lying down—or sitting comfortably if they're fat?</p>

      <p>These design choices aren't neutral. They're political. They communicate who belongs in public space and who doesn't. They enforce a kind of social order through physical barriers.</p>

      <blockquote>
        "The built environment doesn't just reflect our values—it enforces them. Every narrow doorway, every undersized seat, every weight limit is a statement about whose bodies matter."
      </blockquote>

      <h2>A Brief History of Hostile Architecture</h2>

      <p>The term "hostile architecture" typically refers to design features that target homeless people: spikes under bridges, benches with dividers, sloped surfaces that prevent sleeping. But the concept extends far beyond homelessness. It's about designing space to control behavior and exclude certain bodies.</p>

      <p>In the case of fat people, this exclusion is often justified through the language of "health" and "safety." Weight limits on furniture. Narrow aisles in stores. Turnstiles and doorways sized for "average" bodies. But whose average? And who benefits from these restrictions?</p>

      <h2>The Subway Turnstile as Metaphor</h2>

      <p>Let's return to that subway turnstile. When I encounter it, I have three options:</p>

      <ol>
        <li>Try to squeeze through and risk getting stuck, humiliating myself in front of dozens of commuters</li>
        <li>Ask a station agent to open the accessible gate, marking myself as "other" and holding up the line</li>
        <li>Don't take the subway at all</li>
      </ol>

      <p>None of these options are dignified. All of them reinforce the message that public transit—and by extension, public life—isn't designed for bodies like mine.</p>

      <p>The turnstile becomes a literal barrier to participation in urban life. It limits where I can work, where I can go to school, who I can visit, what cultural events I can attend. It shapes the geography of my life.</p>
    `
  };
  return <div className="min-h-screen bg-brand-cream" data-id="element-597">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-100 py-6 sticky top-16 z-40" data-id="element-598">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-599">
          <Link to="/blog" className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-magenta transition-colors" data-id="element-600">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-601" />
            Back to Blog
          </Link>
        </div>
      </header>

      {/* Article */}
      <article className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-602">
        <div className="max-w-4xl mx-auto" data-id="element-603">
          {/* Meta */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-604">
            <span className="inline-block px-3 py-1 bg-brand-magenta text-white text-xs font-bold uppercase tracking-wider mb-6" data-id="element-605">
              {post.category}
            </span>

            <h1 className="text-4xl md:text-6xl font-black leading-tight mb-8 text-brand-black" data-id="element-606">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 pb-8 border-b-2 border-gray-200" data-id="element-607">
              <div className="flex items-center gap-2" data-id="element-608">
                <User className="h-4 w-4" data-id="element-609" />
                <span className="font-medium" data-id="element-610">{post.author.name}</span>
              </div>
              <span className="text-gray-300" data-id="element-611">•</span>
              <div className="flex items-center gap-2" data-id="element-612">
                <Calendar className="h-4 w-4" data-id="element-613" />
                <span data-id="element-614">
                  {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
                </span>
              </div>
              <span className="text-gray-300" data-id="element-615">•</span>
              <div className="flex items-center gap-2" data-id="element-616">
                <Clock className="h-4 w-4" data-id="element-617" />
                <span data-id="element-618">{post.readTime}</span>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="flex items-center gap-4 mb-12 pb-8 border-b border-gray-200" data-id="element-619">
            <button className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-700 hover:text-brand-orange transition-colors border-2 border-gray-200 hover:border-brand-orange" data-id="element-620">
              <Share2 className="h-4 w-4" data-id="element-621" />
              Share
            </button>
            <button className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-700 hover:text-brand-orange transition-colors border-2 border-gray-200 hover:border-brand-orange" data-id="element-622">
              <Bookmark className="h-4 w-4" data-id="element-623" />
              Save
            </button>
          </motion.div>

          {/* Featured Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.3
        }} className="mb-16" data-id="element-624">
            <div className="aspect-[16/9] bg-gradient-to-br from-brand-magenta/30 to-brand-orange/30 border-2 border-brand-black shadow-xl" data-id="element-625"></div>
            <p className="text-sm text-gray-500 mt-4 italic" data-id="element-626">
              Photo illustration: A subway turnstile photographed from the
              perspective of someone approaching it.
            </p>
          </motion.div>

          {/* Content */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{
          __html: post.content
        }} style={{
          fontSize: '1.125rem',
          lineHeight: '1.8',
          color: '#0A0A0A'
        }} data-id="element-627" />

          {/* Pull Quote / Multimedia Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="my-16 p-8 bg-brand-magenta/5 border-l-4 border-brand-magenta" data-id="element-628">
            <blockquote className="text-2xl font-bold text-brand-black leading-relaxed italic" data-id="element-629">
              "The built environment doesn't just reflect our values—it enforces
              them. Every narrow doorway, every undersized seat, every weight
              limit is a statement about whose bodies matter."
            </blockquote>
          </motion.div>

          {/* Video Embed Example */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="my-16" data-id="element-630">
            <div className="aspect-video bg-brand-navy/10 border-2 border-brand-black rounded-lg flex items-center justify-center" data-id="element-631">
              <div className="text-center" data-id="element-632">
                <div className="w-16 h-16 bg-brand-magenta rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-633">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-634">
                    <path d="M8 5v14l11-7z" data-id="element-635" />
                  </svg>
                </div>
                <p className="text-sm font-bold text-gray-600" data-id="element-636">
                  Video: Navigating Public Transit as a Fat Person
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4" data-id="element-637">
              A short documentary exploring the barriers fat people face in
              public transportation systems.
            </p>
          </motion.div>

          {/* Continue reading content */}
          <div className="prose prose-lg max-w-none mb-16" data-id="element-638">
            <h2 data-id="element-639">What Would Inclusive Design Look Like?</h2>
            <p data-id="element-640">
              Imagine a city designed for bodies of all sizes. What would that
              look like?
            </p>
            <ul data-id="element-641">
              <li data-id="element-642">
                Subway turnstiles that accommodate hips wider than 18 inches—or
                better yet, open gates as the default
              </li>
              <li data-id="element-643">
                Restaurant seating with movable tables and chairs of various
                sizes
              </li>
              <li data-id="element-644">
                Airplane seats designed for the reality of human body diversity,
                not the fantasy of the "average" passenger
              </li>
              <li data-id="element-645">
                Park benches without armrests, or with armrests spaced to
                accommodate larger bodies
              </li>
              <li data-id="element-646">
                Doorways, aisles, and corridors wide enough for everyone to move
                through comfortably
              </li>
            </ul>
            <p data-id="element-647">
              This isn't radical. It's basic accessibility. But it requires us
              to expand our definition of accessibility beyond disability to
              include size.
            </p>
          </div>

          {/* Image Gallery Example */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="my-16 grid grid-cols-2 gap-4" data-id="element-648">
            <div className="aspect-square bg-brand-cyan/20 border-2 border-brand-black" data-id="element-649"></div>
            <div className="aspect-square bg-brand-lime/20 border-2 border-brand-black" data-id="element-650"></div>
          </motion.div>

          {/* Final content */}
          <div className="prose prose-lg max-w-none mb-16" data-id="element-651">
            <h2 data-id="element-652">Conclusion: Architecture as Politics</h2>
            <p data-id="element-653">
              The architecture of exclusion isn't just about individual
              buildings or transit systems. It's about a broader political
              project: the control and regulation of bodies in public space.
            </p>
            <p data-id="element-654">
              When we design spaces that exclude fat bodies, we're not just
              making practical decisions about dimensions and weight limits.
              We're making moral and political statements about who deserves to
              participate in public life, who deserves dignity, who deserves to
              move through the world with ease.
            </p>
            <p data-id="element-655">
              Changing this requires more than retrofitting existing spaces. It
              requires reimagining what public space is for and who it's for. It
              requires designing from the start with the full diversity of human
              bodies in mind.
            </p>
            <p data-id="element-656">
              Until then, I'll keep encountering those subway turnstiles. And
              I'll keep asking: Who decided 18 inches was enough? And what would
              it take to decide differently?
            </p>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-16 pb-16 border-b-2 border-gray-200" data-id="element-657">
            <span className="text-xs font-bold uppercase text-gray-500" data-id="element-658">
              Tags:
            </span>
            {['Architecture', 'Public Space', 'Accessibility', 'Urban Planning', 'Hostile Design'].map(tag => <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200" data-id="element-659">
                {tag}
              </span>)}
          </div>

          {/* Author Bio */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white border-2 border-brand-black p-8" data-id="element-660">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-6" data-id="element-661">
              About the Author
            </h3>
            <div className="flex flex-col md:flex-row gap-6" data-id="element-662">
              <div className={`w-32 h-32 rounded-full ${post.author.image} border-2 border-brand-black flex-shrink-0`} data-id="element-663"></div>
              <div data-id="element-664">
                <h4 className="text-2xl font-black mb-3 text-brand-black" data-id="element-665">
                  {post.author.name}
                </h4>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-666">
                  {post.author.bio}
                </p>
                <Link to={`/blog?author=${post.author.name}`} className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-magenta transition-colors" data-id="element-667">
                  More by {post.author.name.split(' ')[0]}{' '}
                  <ArrowRight className="ml-2 h-4 w-4" data-id="element-668" />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Related Posts */}
          <div className="mt-20" data-id="element-669">
            <h3 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-670">
              Related Reading
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-671">
              {[{
              title: "Beyond Representation: Why Seeing Fat People on TV Isn't Enough",
              category: 'essay',
              slug: 'beyond-representation'
            }, {
              title: 'How Schools Weaponize BMI: An Investigation',
              category: 'reporting',
              slug: 'schools-weaponize-bmi'
            }].map(related => <Link key={related.slug} to={`/blog/${related.slug}`} className="group border-l-4 border-brand-magenta bg-white p-6 hover:shadow-lg transition-shadow" data-id="element-672">
                  <span className="text-xs font-bold uppercase text-brand-magenta mb-2 block" data-id="element-673">
                    {related.category}
                  </span>
                  <h4 className="text-lg font-bold text-brand-black group-hover:text-brand-orange transition-colors" data-id="element-674">
                    {related.title}
                  </h4>
                </Link>)}
            </div>
          </div>
        </div>
      </article>
    </div>;
}